import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { countryCodes } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { fetchGeolocation, submitLead, getCountryFromGeo, getDialCodeFromCountryCode } from '../services/api';

const ContactPopup = ({ onClose }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);

  // Fetch geolocation on component mount
  useEffect(() => {
    const loadGeoLocation = async () => {
      const result = await fetchGeolocation();
      if (result.success && result.data) {
        setGeoData(result.data);
        
        // Set country code based on geolocation
        const countryCode = getCountryFromGeo(result.data);
        const dialCode = getDialCodeFromCountryCode(countryCode, countryCodes);
        
        setFormData(prev => ({
          ...prev,
          countryCode: dialCode
        }));
      }
    };
    
    loadGeoLocation();
  }, []);

  const validatePhone = (phone) => {
    // Remove any non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (!phone) {
      return 'Phone number is required';
    }
    if (/[a-zA-Z]/.test(phone)) {
      return 'Phone number cannot contain letters';
    }
    if (digitsOnly.length > 10) {
      return 'Phone number cannot exceed 10 digits';
    }
    if (digitsOnly.length < 10) {
      return 'Phone number must be 10 digits';
    }
    return '';
  };

  const handlePhoneChange = (value) => {
    // Only allow numbers and formatting characters
    const sanitized = value.replace(/[^\d]/g, '');
    if (sanitized.length <= 10) {
      setFormData(prev => ({ ...prev, phone: sanitized }));
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone
    const phoneError = validatePhone(formData.phone);
    if (phoneError) {
      setErrors({ phone: phoneError });
      toast({
        title: "Validation Error",
        description: phoneError,
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit lead to API
      const result = await submitLead(formData, geoData);

      if (result.success) {
        toast({
          title: "Success!",
          description: result.message || "Thank you! Redirecting...",
        });
        
        setFormData({ name: '', email: '', countryCode: '+1', phone: '', message: '' });
        setErrors({});
        onClose();
        
        // Redirect after a short delay
        setTimeout(() => {
          window.location.href = result.redirect;
        }, 1500);
      } else {
        // Show error from API
        toast({
          title: "Error",
          description: result.error || "Failed to submit form. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Network error. Please check your connection and try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-slate-900">Get Started</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <Input
              required
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <Input
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="your.email@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={formData.countryCode} onValueChange={(value) => handleChange('countryCode', value)}>
                <SelectTrigger className="w-full sm:w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map((item) => (
                    <SelectItem key={item.code} value={item.code}>
                      {item.code} {item.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="1234567890"
                maxLength={10}
                className={`flex-1 ${errors.phone ? 'border-red-500' : ''}`}
              />
            </div>
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
            <Textarea
              required
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Tell us about your trademark needs..."
              rows={4}
            />
          </div>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ContactPopup;